import { z } from 'zod/v4';
import { PageDTO } from '@/shared/api/pagination';
import { TQLHILinkTypeDTO } from './hi-link';
import { SeverityDTO } from './severity';

/**
 * Severity interval overlapping the requested window, shared by CI HIS items
 * and SOT-depends items.
 *
 * `started_at` is the absolute interval start. `is_open` means the interval was
 * still active at window end; `finished_at` may still be set (clipped to the
 * window) or null. `sot_id` is null on access-denied dependency rows (see
 * `HISotDependsItemDTO`).
 */
export const HISegmentDTO = z.object({
  severity: SeverityDTO,
  started_at: z.string(),
  finished_at: z.string().nullable(),
  is_open: z.boolean(),
  sot_id: z.string().nullable(),
});

export type HISegment = z.infer<typeof HISegmentDTO>;

/**
 * HI link identity embedded in CI HIS and SOT-depends rows.
 *
 * Declared in the CI HIS form (non-null). `HISotDependsItemDTO` overrides the
 * identity fields to nullable — they are null on access-denied rows.
 */
const CIHisHiLinkDTO = z.object({
  tql_hi_link_id: z.string(),
  hi_type_name: z.string(),
  hi_type_label: z.string(),
  type: TQLHILinkTypeDTO,
  has_depends: z.boolean(),
  node_id: z.string(),
  template_name: z.string(),
  template_label: z.string(),
  assign_ts: z.string(),
  unassign_ts: z.string().nullable(),
});

/**
 * Health-indicator row for a single CI over a time window.
 *
 * Field semantics (not expressible in types alone):
 * - Row is included if the HI had at least one interval in `severities`.
 * - "Not OK" = severity IN (3, 4, 5); independent of the severity filter.
 * - current_severity null is valid (no active indicator at window end).
 * - unassign_ts null means the HI is still assigned.
 * - segment starts are absolute; max_segments may truncate the list.
 */
export const CIHisItemDTO = CIHisHiLinkDTO.extend({
  current_severity: SeverityDTO.nullable(),
  current_status_since: z.string().nullable(),
  current_status_duration_sec: z.number().nullable(),
  worst_severity: SeverityDTO,
  time_not_ok_sec: z.number(),
  transitions_count: z.number(),
  segments: z.array(HISegmentDTO),
});

export type CIHisItem = z.infer<typeof CIHisItemDTO>;

export const CIHisRequestDTO = z.object({
  ci_id: z.string(),
  severities: z.array(z.number()).optional(),
  tql_hi_link_id: z.string().nullish(),
  max_segments: z.number().int().optional(),
  hi_type_name: z.string().nullish(),
});

export type CIHisRequest = z.infer<typeof CIHisRequestDTO>;

/**
 * SOT interval row for the HI chronology grid.
 *
 * Extends `HISegmentDTO` with non-null `sot_id`. `duration_sec` is clipped to
 * the requested window, while `full_duration_sec` is the real (unclipped)
 * duration of the interval.
 */
export const HISotIntervalDTO = HISegmentDTO.extend({
  sot_id: z.string(),
  duration_sec: z.number(),
  full_duration_sec: z.number(),
  clipped_left: z.boolean(),
  clipped_right: z.boolean(),
  alerts_count: z.number(),
  has_depends: z.boolean(),
});

export type HISotInterval = z.infer<typeof HISotIntervalDTO>;

/** Subscription metadata is returned when chronology changes are requested. */
export const HIChronologyResponseDTO = PageDTO.extend({
  items: z.array(HISotIntervalDTO),
  subscribe_id: z.string().nullish(),
  /** Server subscription lifetime in minutes. */
  subscribe_life_time: z.number().nullish(),
});

export type HIChronologyResponse = z.infer<typeof HIChronologyResponseDTO>;

/**
 * SOT dependency row within the requested window (GET /hi_history/sot_depends).
 *
 * A dependency closed off by HI-type permissions or ABAC on the CI stays in
 * the result with `access_denied=true`: status, attach bounds and strip are
 * visible, identity fields are null. Segment `sot_id` is null on such rows.
 */
export const HISotDependsItemDTO = CIHisHiLinkDTO.extend({
  id: z.uuid(),
  tql_hi_link_id: z.string().nullable(),
  ci_id: z.string().nullable(),
  hi_type_name: z.string().nullable(),
  type: TQLHILinkTypeDTO.nullable(),
  access_denied: z.boolean(),
  hi_type_label: z.string().nullable(),
  ci_name: z.string().nullable(),
  node_id: z.string().nullable(),
  template_name: z.string().nullable(),
  template_label: z.string().nullable(),
  severity: SeverityDTO,
  segments: z.array(HISegmentDTO),
});

export type HISotDependsItem = z.infer<typeof HISotDependsItemDTO>;